'use client';

import { c as _c } from "react/compiler-runtime";
import { CircleHelpIcon, RotateCwIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import type { LightmapHarvestReadyReason } from '@/app/sdk';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
export const BLENDER_DOWNLOAD_URL = 'https://www.blender.org/download/';
const BLENDER_NOT_FOUND_MESSAGE = 'Blender was not found. Install Blender or specify its executable path.';
const BLENDER_BAKE_HELP_STEPS: ReactNode[] = [<>
    Wait for Genesys to finish importing — Blender saves{' '}
    <strong className="font-semibold text-secondary-foreground">imported_scene.blend</strong> with The
    Lightmapper prepared.
  </>, <>
    In the <strong className="font-semibold text-secondary-foreground">Properties editor</strong> on the
    right, open <strong className="font-semibold text-secondary-foreground">Render Properties</strong>{' '}
    (camera icon). The Lightmapper panels live under{' '}
    <strong className="font-semibold text-secondary-foreground">The Lightmapper</strong> in this tab.
  </>, <>
    Under{' '}
    <strong className="font-semibold text-secondary-foreground">
      The Lightmapper → Settings → General Settings
    </strong>
    , review global bake options (resolution, samples, and related defaults) before starting a bake.
  </>, <>
    <strong className="font-semibold text-secondary-foreground">Optional — GPU:</strong> Under{' '}
    <strong className="font-semibold text-secondary-foreground">
      The Lightmapper → Settings → Cycle Settings
    </strong>
    , set <strong className="font-semibold text-secondary-foreground">Device</strong> to{' '}
    <strong className="font-semibold text-secondary-foreground">GPU Compute</strong> for faster Cycles
    bakes when your GPU supports it.
  </>, <>
    <strong className="font-semibold text-secondary-foreground">Optional — bulk enable:</strong> Under{' '}
    <strong className="font-semibold text-secondary-foreground">The Lightmapper → Utilities</strong>,
    use the utility tools to enable many objects for lightmap baking at once.
  </>, <>
    <strong className="font-semibold text-secondary-foreground">Per object:</strong> Select a mesh, open{' '}
    <strong className="font-semibold text-secondary-foreground">Object Properties</strong> (cube icon) in
    the Properties editor, and configure options under{' '}
    <strong className="font-semibold text-secondary-foreground">The Lightmapper</strong>.
  </>, <>
    When settings look good, return to{' '}
    <strong className="font-semibold text-secondary-foreground">
      Render Properties → The Lightmapper → Settings → General Settings
    </strong>{' '}
    and click <strong className="font-semibold text-secondary-foreground">Build Lightmaps</strong>. Wait
    for the bake to finish.
  </>, <>
    Save the file (<strong className="font-semibold text-secondary-foreground">File → Save</strong>, or
    Ctrl+S). Keep it as{' '}
    <strong className="font-semibold text-secondary-foreground">imported_scene.blend</strong> — do not
    reimport meshes or delete Genesys lightmap objects before importing back in Genesys.
  </>];
interface BuildLightmapDialogProps {
  open: boolean;
  blenderPath?: string;
  errorMessage?: string;
  invalidSelectionMessage?: string;
  canStart: boolean;
  canBakeInBlender: boolean;
  canCheckBakedScene: boolean;
  canHarvestBuildData: boolean;
  harvestReadyReason?: LightmapHarvestReadyReason;
  isBusy: boolean;
  isCheckingBakedScene: boolean;
  showInstallBlender: boolean;
  onCancel: () => void;
  onInstall: () => void;
  onBrowseForBlender: () => void;
  onBakeInBlender: () => void;
  onCheckForBakedScene: () => void;
  onImportBakedLightmaps: () => void;
  onResetBlenderPath: () => void;
  onDismissInvalidSelection: () => void;
  onStart: () => void;
}
const DialogSection = t0 => {
  const $ = _c(8);
  const {
    title,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-2.5 rounded-lg border border-border/60 bg-muted/30 p-3.5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h3 className="text-2xs font-semibold text-secondary-foreground">{title}</h3>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2) {
    t3 = <section className={t1}>{t2}{children}</section>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
DialogSection.displayName = 'DialogSection';
const BlenderBakeHelpDialog = t0 => {
  const $ = _c(8);
  const {
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogHeader className="gap-1 pb-1"><DialogTitle showSeparator={false}>Bake in Blender</DialogTitle><DialogDescription>Steps in Blender after Genesys opens your scene.</DialogDescription></DialogHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ol className="flex list-none flex-col gap-2.5 text-2xs text-muted-foreground">{BLENDER_BAKE_HELP_STEPS.map(_temp)}</ol>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onOpenChange) {
    t3 = <DialogContent className="max-w-[440px] gap-3">{t1}{t2}<DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Got It</Button></DialogFooter></DialogContent>;
    $[2] = onOpenChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onOpenChange || $[5] !== open || $[6] !== t3) {
    t4 = <Dialog open={open} onOpenChange={onOpenChange}>{t3}</Dialog>;
    $[4] = onOpenChange;
    $[5] = open;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
BlenderBakeHelpDialog.displayName = 'BlenderBakeHelpDialog';
const HARVEST_STATUS_MESSAGES: Record<LightmapHarvestReadyReason, string> = {
  ready: 'Ready — click Import Baked Lightmaps to update your scene.',
  not_saved_after_setup: 'Blender setup finished — bake in TLM, save imported_scene.blend, then check again.',
  missing_session: 'Waiting for Blender to finish importing…',
  missing_blend: 'Waiting for Blender to finish importing…',
  stale_export: 'Scene was re-exported — reopen in Blender and save again.',
  missing_export: 'Use Bake In Blender for this scene first.',
  run_id_mismatch: 'Use Bake In Blender for this scene first.',
  invalid_run_id: 'Use Bake In Blender for this scene first.',
  missing_export_revision: 'Use Bake In Blender for this scene first.'
};
const getHarvestStatusMessage = (harvestReadyReason: LightmapHarvestReadyReason | undefined): string => {
  if (harvestReadyReason) {
    return HARVEST_STATUS_MESSAGES[harvestReadyReason];
  }
  return 'After saving in Blender, use the refresh button beside Import to check readiness.';
};
export const BuildLightmapDialog = t0 => {
  const $ = _c(130);
  const {
    open,
    blenderPath,
    errorMessage,
    invalidSelectionMessage,
    canStart,
    canBakeInBlender,
    canCheckBakedScene,
    canHarvestBuildData,
    harvestReadyReason,
    isBusy,
    isCheckingBakedScene,
    showInstallBlender,
    onCancel,
    onInstall,
    onBrowseForBlender,
    onBakeInBlender,
    onCheckForBakedScene,
    onImportBakedLightmaps,
    onResetBlenderPath,
    onDismissInvalidSelection,
    onStart
  } = t0;
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [blenderBakeHelpOpen, setBlenderBakeHelpOpen] = useState(false);
  let t1;
  if ($[0] !== blenderPath) {
    t1 = blenderPath?.trim();
    $[0] = blenderPath;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const trimmedBlenderPath = t1;
  const dialogBusy = isBusy || isCheckingBakedScene;
  let t2;
  if ($[2] !== harvestReadyReason) {
    t2 = getHarvestStatusMessage(harvestReadyReason);
    $[2] = harvestReadyReason;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const harvestStatusMessage = t2;
  let t3;
  if ($[4] !== dialogBusy || $[5] !== onCancel) {
    t3 = nextOpen => {
      if (!nextOpen && !dialogBusy) {
        onCancel();
      }
    };
    $[4] = dialogBusy;
    $[5] = onCancel;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = !dialogBusy;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogHeader className="gap-1 pb-1"><DialogTitle showSeparator={false}>Build Lightmap</DialogTitle><DialogDescription>Choose a bake workflow after Blender is configured.</DialogDescription></DialogHeader>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== dialogBusy || $[9] !== errorMessage || $[10] !== open) {
    t6 = open && errorMessage && !dialogBusy ? <p className="text-2xs text-destructive">{errorMessage}</p> : null;
    $[8] = dialogBusy;
    $[9] = errorMessage;
    $[10] = open;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = trimmedBlenderPath ? "font-mono" : "font-sans italic";
  let t8;
  if ($[12] !== t7) {
    t8 = cn("break-all rounded-md border border-border bg-muted/50 p-2 text-2xs text-muted-foreground", t7);
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = trimmedBlenderPath ? trimmedBlenderPath : "Not set \u2014 install Blender or browse for its executable below.";
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== open || $[18] !== showInstallBlender) {
    t11 = open && showInstallBlender ? <p className="text-2xs text-destructive">{BLENDER_NOT_FOUND_MESSAGE}</p> : null;
    $[17] = open;
    $[18] = showInstallBlender;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== dialogBusy || $[21] !== onInstall || $[22] !== open || $[23] !== showInstallBlender) {
    t12 = open && showInstallBlender ? <Button type="button" variant="secondary" onClick={onInstall} disabled={dialogBusy}>Install Blender</Button> : null;
    $[20] = dialogBusy;
    $[21] = onInstall;
    $[22] = open;
    $[23] = showInstallBlender;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = isBusy && !isCheckingBakedScene ? "Checking..." : "Browse for Blender";
  let t14;
  if ($[25] !== dialogBusy || $[26] !== onBrowseForBlender || $[27] !== t13) {
    t14 = <Button type="button" variant="outline" onClick={onBrowseForBlender} disabled={dialogBusy}>{t13}</Button>;
    $[25] = dialogBusy;
    $[26] = onBrowseForBlender;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setResetConfirmOpen(true);
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== dialogBusy) {
    t16 = <Button type="button" variant="ghost" onClick={t15} disabled={dialogBusy}>Reset</Button>;
    $[30] = dialogBusy;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t12 || $[33] !== t14 || $[34] !== t16) {
    t17 = <div className="flex flex-wrap items-center gap-2">{t12}{t14}{t16}</div>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t17) {
    t18 = <DialogSection title="Blender">{t10}{t11}{t17}</DialogSection>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p className="text-2xs text-muted-foreground">Bake lightmaps automatically — Genesys exports your scene and runs the bake for you.</p>;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = !canStart || dialogBusy;
  let t21;
  if ($[41] !== onStart || $[42] !== t20) {
    t21 = <DialogSection title="Quick Bake">{t19}<Button type="button" className="w-full" onClick={onStart} disabled={t20}>Quick Bake</Button></DialogSection>;
    $[41] = onStart;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="text-2xs text-muted-foreground">Open Blender to tune lighting and baking, then import the results when you are done.</p>;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="font-semibold text-secondary-foreground">1.</span>;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <li>{t23} Click{" "}<strong className="font-semibold text-secondary-foreground">Bake In Blender</strong>{" "}to open your scene in Blender</li>;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span><span className="font-semibold text-secondary-foreground">2.</span> Configure TLM settings, bake lightmaps, and save in Blender</span>;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  let t27;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <li className="flex flex-wrap items-center gap-1">{t25}<button type="button" className="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-muted/80 text-muted-foreground transition-colors hover:bg-muted hover:text-secondary-foreground" title="How to Bake in Blender" aria-label="How to Bake in Blender" onClick={() => setBlenderBakeHelpOpen(true)}><CircleHelpIcon className="size-2.5" aria-hidden="true" /></button></li>;
    t27 = <span className="font-semibold text-secondary-foreground">3.</span>;
    $[48] = t26;
    $[49] = t27;
  } else {
    t26 = $[48];
    t27 = $[49];
  }
  let t28;
  let t29;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <li>{t27} Click the{" "}<strong className="font-semibold text-secondary-foreground">refresh</strong> button beside Import to verify your save is ready</li>;
    t29 = <span className="font-semibold text-secondary-foreground">4.</span>;
    $[50] = t28;
    $[51] = t29;
  } else {
    t28 = $[50];
    t29 = $[51];
  }
  let t30;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ol className="flex list-none flex-col gap-0.5 text-2xs text-muted-foreground">{t24}{t26}{t28}<li>{t29} Click{" "}<strong className="font-semibold text-secondary-foreground">Import Baked Lightmaps</strong>{" "}to apply them to your project</li></ol>;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  const t31 = canHarvestBuildData && "text-green-400";
  let t32;
  if ($[53] !== t31) {
    t32 = cn("rounded-sm bg-black/20 px-2 py-1.5 text-2xs text-muted-foreground", t31);
    $[53] = t31;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== harvestStatusMessage || $[56] !== t32) {
    t33 = <p className={t32}>{harvestStatusMessage}</p>;
    $[55] = harvestStatusMessage;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  const t34 = dialogBusy || !canBakeInBlender;
  let t35;
  if ($[58] !== onBakeInBlender || $[59] !== t34) {
    t35 = <Button type="button" variant="outline" className="w-full" onClick={onBakeInBlender} disabled={t34}>Bake In Blender</Button>;
    $[58] = onBakeInBlender;
    $[59] = t34;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  const t36 = canHarvestBuildData ? "default" : "secondary";
  const t37 = dialogBusy || !canHarvestBuildData;
  let t38;
  if ($[61] !== onImportBakedLightmaps || $[62] !== t36 || $[63] !== t37) {
    t38 = <Button type="button" variant={t36} className="min-w-0 flex-1" onClick={onImportBakedLightmaps} disabled={t37}>Import Baked Lightmaps</Button>;
    $[61] = onImportBakedLightmaps;
    $[62] = t36;
    $[63] = t37;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  const t39 = dialogBusy || !canCheckBakedScene;
  const t40 = isCheckingBakedScene && "animate-spin";
  let t41;
  if ($[65] !== t40) {
    t41 = cn("size-4", t40);
    $[65] = t40;
    $[66] = t41;
  } else {
    t41 = $[66];
  }
  let t42;
  if ($[67] !== t41) {
    t42 = <RotateCwIcon className={t41} />;
    $[67] = t41;
    $[68] = t42;
  } else {
    t42 = $[68];
  }
  let t43;
  if ($[69] !== onCheckForBakedScene || $[70] !== t39 || $[71] !== t42) {
    t43 = <Button type="button" variant="outline" size="icon" className="size-9 shrink-0" onClick={onCheckForBakedScene} disabled={t39} title="Check for Baked Scene" aria-label="Check for Baked Scene">{t42}</Button>;
    $[69] = onCheckForBakedScene;
    $[70] = t39;
    $[71] = t42;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== t38 || $[74] !== t43) {
    t44 = <div className="flex gap-2">{t38}{t43}</div>;
    $[73] = t38;
    $[74] = t43;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] !== t35 || $[77] !== t44) {
    t45 = <div className="flex flex-col gap-2">{t35}{t44}</div>;
    $[76] = t35;
    $[77] = t44;
    $[78] = t45;
  } else {
    t45 = $[78];
  }
  let t46;
  if ($[79] !== t33 || $[80] !== t45) {
    t46 = <DialogSection title="Interactive Bake">{t22}{t30}{t33}{t45}</DialogSection>;
    $[79] = t33;
    $[80] = t45;
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] !== t18 || $[83] !== t21 || $[84] !== t46) {
    t47 = <div className="flex flex-col gap-3">{t18}{t21}{t46}</div>;
    $[82] = t18;
    $[83] = t21;
    $[84] = t46;
    $[85] = t47;
  } else {
    t47 = $[85];
  }
  let t48;
  if ($[86] !== t4 || $[87] !== t47 || $[88] !== t6) {
    t48 = <DialogContent className="max-w-[520px] gap-4" showCloseButton={t4}>{t5}{t6}{t47}</DialogContent>;
    $[86] = t4;
    $[87] = t47;
    $[88] = t6;
    $[89] = t48;
  } else {
    t48 = $[89];
  }
  let t49;
  if ($[90] !== open || $[91] !== t3 || $[92] !== t48) {
    t49 = <Dialog open={open} onOpenChange={t3}>{t48}</Dialog>;
    $[90] = open;
    $[91] = t3;
    $[92] = t48;
    $[93] = t49;
  } else {
    t49 = $[93];
  }
  const t50 = Boolean(invalidSelectionMessage);
  let t51;
  if ($[94] !== onDismissInvalidSelection) {
    t51 = nextOpen_0 => {
      if (!nextOpen_0) {
        onDismissInvalidSelection();
      }
    };
    $[94] = onDismissInvalidSelection;
    $[95] = t51;
  } else {
    t51 = $[95];
  }
  let t52;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <AlertDialogTitle>Invalid Blender Executable</AlertDialogTitle>;
    $[96] = t52;
  } else {
    t52 = $[96];
  }
  let t53;
  if ($[97] !== invalidSelectionMessage) {
    t53 = <AlertDialogHeader>{t52}<AlertDialogDescription className="break-words">{invalidSelectionMessage}</AlertDialogDescription></AlertDialogHeader>;
    $[97] = invalidSelectionMessage;
    $[98] = t53;
  } else {
    t53 = $[98];
  }
  let t54;
  if ($[99] !== isBusy || $[100] !== onDismissInvalidSelection) {
    t54 = <AlertDialogFooter><AlertDialogAction disabled={isBusy} onClick={onDismissInvalidSelection}>OK</AlertDialogAction></AlertDialogFooter>;
    $[99] = isBusy;
    $[100] = onDismissInvalidSelection;
    $[101] = t54;
  } else {
    t54 = $[101];
  }
  let t55;
  if ($[102] !== t53 || $[103] !== t54) {
    t55 = <AlertDialogContent>{t53}{t54}</AlertDialogContent>;
    $[102] = t53;
    $[103] = t54;
    $[104] = t55;
  } else {
    t55 = $[104];
  }
  let t56;
  if ($[105] !== t50 || $[106] !== t51 || $[107] !== t55) {
    t56 = <AlertDialog open={t50} onOpenChange={t51}>{t55}</AlertDialog>;
    $[105] = t50;
    $[106] = t51;
    $[107] = t55;
    $[108] = t56;
  } else {
    t56 = $[108];
  }
  let t57;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <AlertDialogHeader><AlertDialogTitle>Reset Blender Path?</AlertDialogTitle><AlertDialogDescription>Your saved Blender executable path will be removed. The app will look for Blender in standard install locations instead.</AlertDialogDescription></AlertDialogHeader>;
    $[109] = t57;
  } else {
    t57 = $[109];
  }
  let t58;
  if ($[110] !== isBusy) {
    t58 = <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>;
    $[110] = isBusy;
    $[111] = t58;
  } else {
    t58 = $[111];
  }
  let t59;
  if ($[112] !== onResetBlenderPath) {
    t59 = () => {
      setResetConfirmOpen(false);
      onResetBlenderPath();
    };
    $[112] = onResetBlenderPath;
    $[113] = t59;
  } else {
    t59 = $[113];
  }
  let t60;
  if ($[114] !== isBusy || $[115] !== t59) {
    t60 = <AlertDialogAction disabled={isBusy} onClick={t59}>Reset</AlertDialogAction>;
    $[114] = isBusy;
    $[115] = t59;
    $[116] = t60;
  } else {
    t60 = $[116];
  }
  let t61;
  if ($[117] !== t58 || $[118] !== t60) {
    t61 = <AlertDialogContent>{t57}<AlertDialogFooter>{t58}{t60}</AlertDialogFooter></AlertDialogContent>;
    $[117] = t58;
    $[118] = t60;
    $[119] = t61;
  } else {
    t61 = $[119];
  }
  let t62;
  if ($[120] !== resetConfirmOpen || $[121] !== t61) {
    t62 = <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>{t61}</AlertDialog>;
    $[120] = resetConfirmOpen;
    $[121] = t61;
    $[122] = t62;
  } else {
    t62 = $[122];
  }
  let t63;
  if ($[123] !== blenderBakeHelpOpen) {
    t63 = <BlenderBakeHelpDialog open={blenderBakeHelpOpen} onOpenChange={setBlenderBakeHelpOpen} />;
    $[123] = blenderBakeHelpOpen;
    $[124] = t63;
  } else {
    t63 = $[124];
  }
  let t64;
  if ($[125] !== t49 || $[126] !== t56 || $[127] !== t62 || $[128] !== t63) {
    t64 = <>{t49}{t56}{t62}{t63}</>;
    $[125] = t49;
    $[126] = t56;
    $[127] = t62;
    $[128] = t63;
    $[129] = t64;
  } else {
    t64 = $[129];
  }
  return t64;
};
BuildLightmapDialog.displayName = 'BuildLightmapDialog';
function _temp(step, index) {
  return <li key={index} className="flex gap-2"><span className="shrink-0 font-semibold text-secondary-foreground">{index + 1}.</span><span>{step}</span></li>;
}